import { CloseIcon } from 'components/icons'
import { Button } from 'components/ui'
import { useApp } from 'context/app-context'
import { useCurrentUser } from 'context/current-user-context'
import classNames from 'helpers/class-names'

/** Global cache to support showing notifications only once. */
const seenNotifications = new Set<string>()

/** Returns true if notification has been seen. */
export function hasSeenNotification(id: string) {
  if (seenNotifications.has(id)) {
    return true
  }
  const value = Number(window.localStorage.getItem(id))
  // timestamp should be specific to the notification though
  if (Number.isFinite(value) && Date.now() / 1000 - value < 24 * 60 * 60) {
    seenNotifications.add(id)
    return true
  }
  return false
}

/** Tags notification as seen. */
export function setSeenNotification(id: string) {
  if (!hasSeenNotification(id)) {
    seenNotifications.add(id)
    window.localStorage.setItem(id, Math.round(Date.now() / 1000).toString())
  }
}

/** Notification component. */
const Notification = () => {
  const { notification, setNotification } = useApp()
  const { isExpired, showOnboarding } = useCurrentUser()

  if (!notification || isExpired || showOnboarding) {
    return null
  }

  return (
    <div
      role="alert"
      className={classNames(
        'fixed bottom-6 right-6 z-50 flex rounded-lg',
        'max-w-sm flex-col border bg-white font-body shadow-md',
      )}>
      <div
        className={classNames(
          'flex w-full max-w-lg flex-col gap-1 rounded-lg px-2 py-3',
        )}>
        <div className="flex-1 whitespace-pre-line break-words px-2 text-sm font-bold text-gray-500">
          {notification.title}
        </div>
        <button
          className="flex-0 absolute right-2 top-2 shrink-0 cursor-pointer rounded-lg p-0.5 text-left outline-none
                     transition-all duration-300 focus-visible:ring-2 focus-visible:ring-info/75 active:scale-90"
          onClick={() => {
            setNotification(null)
            notification.dismiss?.()
          }}
          title="Close">
          <CloseIcon className="size-5 text-gray-300" />
        </button>
        <div className="flex-1 whitespace-pre-line break-words px-2 text-sm font-semibold text-gray-500">
          {notification.message}
        </div>
        <div className="mt-1 flex flex-wrap items-center justify-end gap-2 px-1">
          {notification.buttons.map((button, index) => {
            return (
              <Button
                className="rounded-lg py-2"
                primary
                key={`notification-${index}`}
                title={button.title}
                onClick={(e) => {
                  setNotification(null)
                  button.action?.(e)
                }}>
                {button.title}
              </Button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default Notification
